import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  RowSelectionState,
  useReactTable,
} from "@tanstack/react-table";
import { Dispatch, SetStateAction, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./table";
import { Button } from "./button";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import {
  Select,
  SelectItem,
  SelectTrigger,
  SelectContent,
  SelectValue,
} from "./select";

interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T, any>[]; //table column header definition
  rowSelection: RowSelectionState; //checkbox state for each row
  setRowSelection: Dispatch<SetStateAction<RowSelectionState>>; //checkbox state managing for each row
  onRowSelect?: (selectedRow: T[]) => void; //rows state that are checked
  globalFilter?: string;
  setGlobalFilter?: Dispatch<SetStateAction<string>>;
  globalFilterFn?: FilterFn<T>;
  columnFilters: ColumnFiltersState;
  setColumnFilters: Dispatch<SetStateAction<ColumnFiltersState>>;
  onTableInstance?: (table: ReturnType<typeof useReactTable<T>>) => void;
  showSelectedIndicator?: boolean;
}

const DataTable = <T extends { id: string | number }>({
  data,
  columns,
  rowSelection,
  setRowSelection,
  onRowSelect,
  globalFilter,
  setGlobalFilter,
  globalFilterFn,
  columnFilters,
  setColumnFilters,
  onTableInstance,
  showSelectedIndicator = true
}: DataTableProps<T>) => {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: (updater) => {
      const newRowSelection =
        typeof updater === "function" ? updater(rowSelection) : updater;
      setRowSelection(newRowSelection);

      if (onRowSelect) {
        const selectedRows = data.filter(
          (row) => newRowSelection[String(row.id)]
        );
        onRowSelect(selectedRows);
      }
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn,
    state: {
      rowSelection,
      columnFilters,
      globalFilter,
    },
    getRowId: (row) => String(row.id),
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  const handlePageChange = (value: string) => {
    const pageNum = parseInt(value, 10);
    table.setPageIndex(pageNum - 1);
  };
  const pageCount = table.getPageCount();
  const pageOptions = Array.from({ length: pageCount }, (_, i) => i + 1);

  useEffect(() => {
    if (onTableInstance) {
      onTableInstance(table);
    }
  }, [table, onTableInstance]);

  return (
    <div className="space-y-4">
      {/* Table */}
      <div className="rounded-md border max-h-fit overflow-y-auto">
        <Table>
          <TableHeader className="sticky top-0 z-99">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="px-4 py-2.5">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No Results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className={`flex items-center ${showSelectedIndicator ? "justify-between" : "justify-end"}`}>
        {showSelectedIndicator && (
          <div className="flex-1 text-sm text-muted-foreground">
          {table.getFilteredSelectedRowModel().rows.length} of{" "}
          {table.getFilteredRowModel().rows.length} row(s) selected.
        </div>
        )}

        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.firstPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <ChevronsLeft />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <ChevronLeft />
          </Button>
          {data.length > 0 && (
            <Select
              value={String(table.getState().pagination.pageIndex + 1)}
              onValueChange={handlePageChange}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={table.getState().pagination.pageIndex + 1}
                />
              </SelectTrigger>
              <SelectContent>
                {pageOptions.map((page) => (
                  <SelectItem key={page} value={String(page)}>
                    Page {page}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <ChevronRight />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.lastPage()}
            disabled={!table.getCanNextPage()}
          >
            <ChevronsRight />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DataTable;
